/** The customer's colour applied to the workspace. One brand colour comes from config; everything the UI needs is
 *  derived from it, so text on a coloured surface always passes contrast (a brand cyan rarely does on its own). */

export interface TenantTheme { accent: string }

const clamp = (n: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, n));

export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const toHex = (rgb: [number, number, number]) => "#" + rgb.map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0")).join("");

/** Relative luminance (WCAG 2.1). */
export function luminance([r, g, b]: [number, number, number]): number {
  const f = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrast(a: [number, number, number], b: [number, number, number]): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}

const WHITE: [number, number, number] = [255, 255, 255];
const mix = (a: [number, number, number], b: [number, number, number], t: number): [number, number, number] =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** The brand colour darkened just far enough for white text to pass 4.5:1 on it. */
export function readable(brand: [number, number, number], ratio = 4.5): [number, number, number] {
  let c = brand;
  for (let i = 0; i < 24 && contrast(c, WHITE) < ratio; i++) c = mix(c, [0, 0, 0], 0.08);
  return c;
}

export function themeVars(accent: string): Record<string, string> | null {
  const brand = parseHex(accent);
  if (!brand) return null;
  const ink = readable(brand);
  return {
    "--brand-accent": toHex(brand),                      // the customer's colour, for marks and dots
    "--accent": toHex(ink),                              // links, primary buttons, focus: carries text
    "--accent-ink": contrast(ink, WHITE) >= 4.5 ? "#ffffff" : "#111111",
    "--info-bg": toHex(mix(brand, WHITE, 0.88)),         // the tint behind selected rows and quiet panels
  };
}

/** Paint the customer's colour onto the document; call with null to leave the workspace palette alone. */
export function applyTenantTheme(theme: TenantTheme | null | undefined, root?: HTMLElement): void {
  const el = root ?? (typeof document === "undefined" ? null : document.documentElement);
  if (!el) return;
  const vars = theme?.accent ? themeVars(theme.accent) : null;
  for (const name of ["--brand-accent", "--accent", "--accent-ink", "--info-bg"]) el.style.removeProperty(name);
  if (vars) for (const [name, value] of Object.entries(vars)) el.style.setProperty(name, value);
}
